import { Feature, Op, WorkspaceRole, canRoleDo } from '@trulyinbox/types';

/**
 * The (Feature, Op) pair the API's `RoleAccessGuard` checks for a route. The web
 * asks the SAME static map (`@trulyinbox/types` `ROLE_ACCESS`) so a control a role
 * can't use is removed from the DOM instead of 403-ing on click.
 * See docs/plans/2026-09-29-two-guard-access-model.md §6.
 */
export type Access = { feature: Feature; op: Op };

const at = (feature: Feature, op: Op): Access => ({ feature, op });

/**
 * Every (Feature, Op) the UI gates on, named once. Pick the entry by the API
 * route the control calls (see `role-floors.snapshot.json`); where a surface
 * needs several routes, name the MOST restrictive one it needs to render.
 */
export const ACCESS = {
  // Pages / nav destinations
  apiKeys: at(Feature.API_KEYS, Op.GET),
  apiKeyCreate: at(Feature.API_KEYS, Op.POST),
  apiKeyRevoke: at(Feature.API_KEYS, Op.DELETE),
  audit: at(Feature.AUDIT, Op.GET),
  partner: at(Feature.PARTNER, Op.GET),
  connectAccount: at(Feature.EMAIL_ACCOUNT_CONNECTION, Op.GET),
  // Billing actions (checkout, cancel, pause, ...) and the Plans tab: OWNER
  billingManage: at(Feature.BILLING, Op.POST),
  // Settings sections
  orgRead: at(Feature.ORGANIZATION, Op.GET),
  orgMembers: at(Feature.ORGANIZATION_MEMBERS, Op.GET),
  team: at(Feature.TEAM, Op.GET),
  workspaces: at(Feature.WORKSPACES, Op.GET),
  // Settings actions
  // Pending-invites list (`GET .../invites`): ADMIN, unlike the VIEWER members list
  teamInvites: at(Feature.TEAM_INVITES, Op.GET),
  teamManage: at(Feature.TEAM, Op.POST),
  workspaceCreate: at(Feature.WORKSPACES, Op.POST),
  workspaceUpdate: at(Feature.WORKSPACES, Op.PATCH),
  workspaceDelete: at(Feature.WORKSPACES, Op.DELETE),
  workspaceQuotaUpdate: at(Feature.WORKSPACE_QUOTA, Op.PATCH),
  orgUpdate: at(Feature.ORGANIZATION, Op.PATCH),
  // Email accounts
  accountWrite: at(Feature.EMAIL_ACCOUNTS, Op.PATCH),
  accountCreate: at(Feature.EMAIL_ACCOUNTS, Op.POST),
  accountTags: at(Feature.TAGS, Op.PUT),
  accountDelete: at(Feature.EMAIL_ACCOUNTS, Op.DELETE),
  bulkConnect: at(Feature.EMAIL_ACCOUNTS_BULK_CONNECT, Op.POST),
  dwdAdmin: at(Feature.DWD_WORKSPACE_ADMIN, Op.POST),
  saleshandyLink: at(Feature.SALESHANDY, Op.POST),
  // Template library (server routes are off today; the UI is "Coming soon")
  templateCreate: at(Feature.TEMPLATE, Op.POST),
  templateUpdate: at(Feature.TEMPLATE, Op.PATCH),
  templateDelete: at(Feature.TEMPLATE, Op.DELETE),
} as const satisfies Record<string, Access>;

const KNOWN_ROLES: readonly string[] = Object.values(WorkspaceRole);

/**
 * May `role` (the profile's role string) perform `access`? No `access` means the
 * control has no role requirement. An unknown/absent role denies.
 */
export function roleCan(role: string | null | undefined, access?: Access | null): boolean {
  if (!access) return true;
  if (!role || !KNOWN_ROLES.includes(role)) return false;
  return canRoleDo(role as WorkspaceRole, access.feature, access.op);
}
